import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Eye, Package, Printer, ShoppingCart, Search, X, Image, Video, Link as LinkIcon, Play } from 'lucide-react';
import { FileUploader } from './FileUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminShowcases = ({ showcases, onRefresh }) => {
  const { token } = useAuth();
  const { t, i18n } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShowcase, setEditingShowcase] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contentLang, setContentLang] = useState('zh');
  const [formData, setFormData] = useState({
    title: '', title_en: '', title_ru: '',
    subtitle: '', subtitle_en: '', subtitle_ru: '',
    media_type: 'image', media_url: '',
    link_url: '', link_text: '了解更多', link_text_en: 'Learn More', link_text_ru: 'Узнать больше',
    sort_order: 0, is_active: true
  });

  const resetForm = () => {
    setFormData({
      title: '', title_en: '', title_ru: '',
      subtitle: '', subtitle_en: '', subtitle_ru: '',
      media_type: 'image', media_url: '',
      link_url: '', link_text: '了解更多', link_text_en: 'Learn More', link_text_ru: 'Узнать больше',
      sort_order: 0, is_active: true
    });
    setEditingShowcase(null);
    setContentLang('zh');
  };

  const openCreateDialog = () => { resetForm(); setDialogOpen(true); };

  const openEditDialog = (showcase) => {
    setEditingShowcase(showcase);
    setFormData({
      title: showcase.title || '', title_en: showcase.title_en || '', title_ru: showcase.title_ru || '',
      subtitle: showcase.subtitle || '', subtitle_en: showcase.subtitle_en || '', subtitle_ru: showcase.subtitle_ru || '',
      media_type: showcase.media_type || 'image', media_url: showcase.media_url || '',
      link_url: showcase.link_url || '', link_text: showcase.link_text || '了解更多',
      link_text_en: showcase.link_text_en || 'Learn More', link_text_ru: showcase.link_text_ru || 'Узнать больше',
      sort_order: showcase.sort_order || 0, is_active: showcase.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.media_url) {
      toast.error(t('admin.requiredFields', 'Please fill in required fields'));
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingShowcase) {
        await axios.put(`${API}/showcases/${editingShowcase.id}`, formData, { headers });
        toast.success(t('admin.showcaseUpdateSuccess'));
      } else {
        await axios.post(`${API}/showcases`, formData, { headers });
        toast.success(t('admin.showcaseCreateSuccess'));
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.confirmDeleteShowcase', 'Delete this showcase?'))) return;
    try {
      await axios.delete(`${API}/showcases/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('admin.showcaseDeleteSuccess'));
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const getTitleField = () => contentLang === 'en' ? 'title_en' : contentLang === 'ru' ? 'title_ru' : 'title';
  const getSubtitleField = () => contentLang === 'en' ? 'subtitle_en' : contentLang === 'ru' ? 'subtitle_ru' : 'subtitle';
  const getLinkTextField = () => contentLang === 'en' ? 'link_text_en' : contentLang === 'ru' ? 'link_text_ru' : 'link_text';

  return (
    <div data-testid="admin-showcases">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('admin.showcases')}</h1>
        <Button onClick={openCreateDialog} data-testid="create-showcase-btn">
          <Plus className="w-4 h-4 mr-2" /> {t('admin.addShowcase', 'Add Showcase')}
        </Button>
      </div>

      <div className="grid gap-4">
        {showcases.length === 0 ? (
          <Card className="p-8 text-center text-gray-500">{t('admin.noShowcases', 'No showcases yet')}</Card>
        ) : (
          showcases.map((showcase) => (
            <Card key={showcase.id} className="overflow-hidden">
              <div className="flex">
                <div className="w-48 h-32 bg-gray-100 flex-shrink-0">
                  {showcase.media_type === 'video' ? (
                    <video src={showcase.media_url} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={showcase.media_url} alt={showcase.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <CardContent className="flex-1 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{showcase.title}</h3>
                      {showcase.subtitle && <p className="text-gray-500 text-sm">{showcase.subtitle}</p>}
                      <div className="flex gap-2 mt-2">
                        <Badge variant={showcase.is_active ? 'default' : 'secondary'}>
                          {showcase.is_active ? t('admin.active', 'Active') : t('admin.inactive', 'Inactive')}
                        </Badge>
                        <Badge variant="outline">{t('admin.sortOrder', 'Order')}: {showcase.sort_order}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(showcase)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(showcase.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingShowcase ? t('admin.editShowcase', 'Edit Showcase') : t('admin.addShowcase', 'Add Showcase')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2 mb-4">
              {['zh', 'en', 'ru'].map((lang) => (
                <Button key={lang} variant={contentLang === lang ? 'default' : 'outline'} size="sm" onClick={() => setContentLang(lang)}>
                  {lang === 'zh' ? '中文' : lang === 'en' ? 'English' : 'Русский'}
                </Button>
              ))}
            </div>
            <div className="space-y-2">
              <Label>{t('admin.showcaseTitle')} *</Label>
              <Input value={formData[getTitleField()]} onChange={(e) => setFormData({ ...formData, [getTitleField()]: e.target.value })} placeholder={t('admin.showcaseTitle')} />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.showcaseSubtitle')}</Label>
              <Input value={formData[getSubtitleField()]} onChange={(e) => setFormData({ ...formData, [getSubtitleField()]: e.target.value })} placeholder={t('admin.showcaseSubtitle')} />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.linkText', 'Button Text')}</Label>
              <Input value={formData[getLinkTextField()]} onChange={(e) => setFormData({ ...formData, [getLinkTextField()]: e.target.value })} placeholder={t('admin.linkText')} />
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>{t('admin.mediaType', 'Media Type')}</Label>
              <Select value={formData.media_type} onValueChange={(v) => setFormData({ ...formData, media_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">{t('admin.image', 'Image')}</SelectItem>
                  <SelectItem value="video">{t('admin.video', 'Video')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('admin.mediaUrl', 'Media URL')} *</Label>
              <div className="flex gap-2">
                <Input value={formData.media_url} onChange={(e) => setFormData({ ...formData, media_url: e.target.value })} placeholder="https://..." className="flex-1" />
                <FileUploader type={formData.media_type} accept={formData.media_type === 'video' ? 'video/*' : 'image/*'} onUpload={(url) => setFormData({ ...formData, media_url: url })} label={t('admin.upload', 'Upload')} />
              </div>
              {formData.media_url && (
                <div className="mt-2">
                  {formData.media_type === 'video' ? (
                    <video src={formData.media_url} className="w-full max-h-48 object-contain rounded" controls />
                  ) : (
                    <img src={formData.media_url} alt="Preview" className="w-full max-h-48 object-contain rounded" />
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t('admin.linkUrl', 'Link URL')}</Label>
              <Input value={formData.link_url} onChange={(e) => setFormData({ ...formData, link_url: e.target.value })} placeholder="/artworks or https://..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.sortOrder', 'Sort Order')}</Label>
                <Input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.status', 'Status')}</Label>
                <Select value={formData.is_active ? 'active' : 'inactive'} onValueChange={(v) => setFormData({ ...formData, is_active: v === 'active' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('admin.active', 'Active')}</SelectItem>
                    <SelectItem value="inactive">{t('admin.inactive', 'Inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};



export default AdminShowcases;
